/**
 * O que faz: lista "Para revisar" com sugestões de retorno e de etapa; cada item abre a conversa do lead.
 * Onde usa: ModalRevisaoAgentes (coluna da esquerda) e a prévia do card "Para revisar" na aba Agentes.
 * Conexões: recebe os itens prontos (useRevisaoAgentes fica no dono); não chama o banco.
 * Tabelas: nenhuma direta.
 * Data: 25/09/2026 (virou lista selecionável do modal de revisão em 26/09/2026).
 */
import { CalendarClock, ChevronRight, MoveRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TEXTO_MARCO, type ItemRevisao } from '../contratos';
import { chaveItemRevisao, resumoSugestao } from '../revisao';

interface ListaRevisaoAgentesProps {
  itens: ItemRevisao[];
  selecionadoChave?: string | null;
  onSelecionar: (item: ItemRevisao) => void;
  // Na prévia do card a lista fica mais baixa (sem motivo), para não repetir o modal na página.
  compacta?: boolean;
  desabilitado?: boolean;
}

export function ListaRevisaoAgentes({ itens, selecionadoChave = null, onSelecionar, compacta = false, desabilitado = false }: ListaRevisaoAgentesProps) {
  return (
    <ul className="divide-y divide-border">
      {itens.map((item, indice) => {
        const chave = chaveItemRevisao(item);
        const selecionado = chave === selecionadoChave;
        return (
          // Itens entram em cascata curta (até 8 × 30 ms); ao decidir, a lista nova aparece sem pulo.
          <li key={chave} style={{ animationDelay: `${Math.min(indice, 8) * 30}ms` }} className="animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both duration-200 motion-reduce:animate-none">
            <button
              type="button"
              disabled={desabilitado}
              aria-pressed={selecionado}
              aria-label={`Revisar ${item.tipo === 'retorno' ? 'retorno sugerido' : 'etapa sugerida'} de ${item.leadNome ?? 'lead sem nome'}`}
              onClick={() => onSelecionar(item)}
              className={cn(
                'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50',
                selecionado && 'bg-accent',
              )}
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">{item.leadNome ?? 'Lead sem nome'}</span>
                  {item.tipo === 'retorno'
                    ? <span className="lg-selo lg-selo--info gap-1"><CalendarClock className="h-3 w-3" aria-hidden="true" />Retorno</span>
                    // Sugestão por regra de coluna não tem marco do funil: mostrar "Precisa de revisão" confundiria.
                    : item.personalizada
                      ? <span className="lg-selo lg-selo--neutro">Coluna</span>
                      : <span className="lg-selo lg-selo--neutro">{item.marco ? TEXTO_MARCO[item.marco].rotulo : 'Etapa'}</span>}
                </div>
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  {item.tipo === 'etapa' && <MoveRight className="h-3 w-3 shrink-0" aria-hidden="true" />}
                  <span className="truncate">{resumoSugestao(item)}</span>
                </p>
                {!compacta && item.motivo && <p className="line-clamp-2 text-xs text-foreground/80">{item.motivo}</p>}
                {!compacta && item.personalizada && item.criterio && <p className="line-clamp-2 text-xs text-foreground/80">Critério: {item.criterio}</p>}
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
